import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Check,
  RefreshCcw,
  Star,
  X as XIcon,
  Loader2,
  EyeOff,
  Eye,
} from "lucide-react";
import { adminAuthHeader } from "../../lib/adminAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuthHeaders = () => adminAuthHeader();

const STATUS_TABS = [
  { id: "pending", label: "Pending" },
  { id: "published", label: "Published" },
  { id: "rejected", label: "Rejected" },
];

export default function ReviewsTab() {
  const [loading, setLoading] = useState(true);
  const [statusTab, setStatusTab] = useState("pending");
  const [reviews, setReviews] = useState([]);

  const fetchReviews = async () => {
    setLoading(true);
    try {
      // The existing admin endpoint already returns ALL reviews (including
      // inactive). We filter client-side for clarity , small dataset.
      const r = await axios.get(`${API}/admin/reviews`, {
        headers: getAuthHeaders(),
      });
      setReviews(r.data || []);
    } catch {
      toast.error("Failed to load reviews.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  const filtered = useMemo(() => {
    return reviews.filter((r) => {
      const status = r.status || (r.is_active ? "published" : "rejected");
      return status === statusTab;
    });
  }, [reviews, statusTab]);

  const publish = async (id) => {
    try {
      await axios.post(
        `${API}/admin/reviews/${id}/publish`,
        {},
        { headers: getAuthHeaders() },
      );
      toast.success("Published , now live on /reviews.");
      fetchReviews();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't publish.");
    }
  };

  const reject = async (id) => {
    try {
      await axios.post(
        `${API}/admin/reviews/${id}/reject`,
        {},
        { headers: getAuthHeaders() },
      );
      toast.success("Rejected , hidden from /reviews.");
      fetchReviews();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't reject.");
    }
  };

  const pendingCount = reviews.filter(
    (r) => (r.status || (r.is_active ? "published" : "rejected")) === "pending",
  ).length;

  return (
    <div data-testid="admin-reviews-tab">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-['Outfit'] font-bold text-[var(--charcoal)]">
            Reviews
          </h1>
          <p className="text-sm text-[var(--text-muted)] mt-1">
            Review and publish testimonials submitted by participants.
          </p>
        </div>
        <button
          onClick={fetchReviews}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[var(--light-border)] bg-white text-[var(--charcoal)] text-sm hover:bg-[var(--bone-soft)]"
          data-testid="admin-reviews-refresh"
        >
          <RefreshCcw size={14} />
          Refresh
        </button>
      </div>

      {/* Status tabs */}
      <div className="flex flex-wrap gap-2 mb-5">
        {STATUS_TABS.map((s) => (
          <button
            key={s.id}
            onClick={() => setStatusTab(s.id)}
            className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
              statusTab === s.id
                ? "bg-[var(--charcoal)] text-white border-[var(--charcoal)]"
                : "bg-white text-[var(--charcoal)] border-[var(--light-border)] hover:bg-[var(--bone-soft)]"
            }`}
            data-testid={`admin-reviews-tab-${s.id}`}
          >
            {s.label}
            {s.id === "pending" && pendingCount > 0 && (
              <span className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[var(--lime)] text-[var(--charcoal)] text-[11px] font-bold">
                {pendingCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-sm text-[var(--text-muted)] py-8">
          <Loader2 size={16} className="animate-spin" /> Loading…
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="rounded-2xl border border-[var(--light-border)] bg-white p-10 text-center text-sm text-[var(--text-muted)]">
          No {statusTab} reviews.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filtered.map((r) => (
          <article
            key={r.id}
            className="rounded-2xl border border-[var(--light-border)] bg-white p-5 flex flex-col gap-3"
            data-testid={`admin-review-${r.id}`}
          >
            <header className="flex items-start justify-between gap-3">
              <div>
                <p className="font-semibold text-[var(--charcoal)] text-[15px] leading-tight">
                  {r.name || "Anonymous"}
                </p>
                <p className="text-[12px] text-[var(--text-muted)]">
                  {r.role || "Participant"}
                  {r.condition ? ` · ${r.condition}` : ""}
                  {r.country ? ` · ${r.country}` : ""}
                </p>
              </div>
              <div
                className="flex items-center gap-0.5 text-[var(--lime-dark)]"
                aria-label={`${r.rating} of 5 stars`}
              >
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    size={14}
                    className={
                      i < (r.rating || 5)
                        ? "fill-[var(--lime)] text-[var(--lime-dark)]"
                        : "text-[var(--light-border)]"
                    }
                  />
                ))}
              </div>
            </header>

            <p className="text-[14px] text-[var(--charcoal)] leading-relaxed whitespace-pre-wrap">
              {r.content}
            </p>

            <div className="flex items-center justify-between gap-2 mt-1 pt-3 border-t border-[var(--light-border)] text-[12px] text-[var(--text-muted)]">
              <span>
                {r.source === "kudobuzz"
                  ? "Imported from Kudobuzz"
                  : r.source === "request"
                  ? "From request link"
                  : "Manually added"}
                {r.created_at ? ` · ${new Date(r.created_at).toLocaleString()}` : ""}
              </span>
              <div className="flex items-center gap-2">
                {statusTab !== "published" && (
                  <button
                    onClick={() => publish(r.id)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[var(--lime)] text-[var(--charcoal)] text-[12px] font-semibold hover:bg-[var(--lime-dark)]"
                    data-testid={`admin-review-publish-${r.id}`}
                  >
                    <Check size={12} strokeWidth={3} />
                    Publish
                  </button>
                )}
                {statusTab !== "rejected" && (
                  <button
                    onClick={() => reject(r.id)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-red-700 text-[12px] font-semibold border border-red-200 hover:bg-red-50"
                    data-testid={`admin-review-reject-${r.id}`}
                  >
                    {statusTab === "published" ? <EyeOff size={12} /> : <XIcon size={12} />}
                    {statusTab === "published" ? "Unpublish" : "Reject"}
                  </button>
                )}
                {statusTab === "rejected" && (
                  <button
                    onClick={() => publish(r.id)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-[var(--charcoal)] text-[12px] font-semibold border border-[var(--light-border)] hover:bg-[var(--bone-soft)]"
                  >
                    <Eye size={12} />
                    Restore
                  </button>
                )}
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
